import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './ReminderToVerifyPhoneNumberModal.styles.scss';

type ReminderToVerifyPhoneNumberModalProps = {
  className?: string;
  onVerifyClick: () => void;
} & ModalProps;

const ReminderToVerifyPhoneNumberModal: React.ForwardRefRenderFunction<
  ModalRef,
  ReminderToVerifyPhoneNumberModalProps
> = (props, ref) => {
  const { className, onVerifyClick, ...rest } = props;

  const { setCurrentUser } = useContext(CurrentUserContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames(
    'reminder-to-verify-phone-number-modal',
    className,
  );

  const { t } = useTranslation();

  const remindMeLater = useCallback(() => {
    api.users.remindMeLaterToVerifyPhone();
    setCurrentUser((old) => ({
      ...old,
      hasRemindedToVerifyPhone: true,
    }));
    modal?.close();
  }, [modal, setCurrentUser]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('verifyYourPhoneNumber')}
      onX={remindMeLater}
      onOverlayClose={remindMeLater}
      {...rest}
    >
      <p>{t('verifyYourPhoneNumberDescription')}</p>

      <p className="description">{t('verifyPhoneNumberInfo')}</p>

      <div className="actions">
        <Button variant="link" onClick={remindMeLater}>
          {t('remindMeLater')}
        </Button>

        <Button
          onClick={() => {
            onVerifyClick();
            modal?.close();
          }}
        >
          {t('verifyPhoneNumber')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(ReminderToVerifyPhoneNumberModal);
